import { ISanityVendorConfigs } from '@rbi-ctg/menu';
import {
  ILoyaltyInRestaurantIncentiveInput,
  ILoyaltyInRestaurantOrderLineItem,
  ILoyaltyRbiVendorConfigsInput,
  CartEntryType as LegacyCartEntryTypeGateway,
  LoyaltyCartDiscountType,
} from 'generated/graphql-gateway';
import { IOfferDiscount } from 'generated/sanity-graphql';
import { isCombo, isItem, isPicker } from 'utils/menu/is-menu-type';
import { blockContentToPlainText } from 'utils/sanity';
import { PosVendors } from 'utils/vendor-config';

import { LoyaltyAppliedOffer } from '../types';

import {
  ICartEntryType,
  getInRestaurantRewardDocumentId,
  getInRestaurantRewardId,
} from './hooks/use-in-restaurant-redemption-cart';
import type {
  ICart,
  ICartEntryOffer,
  ICartEntryReward,
  ILegacyCartEntry,
} from './hooks/use-in-restaurant-redemption-cart';

// the accepted BK gateway vendors
const lineItemVendors: ReadonlyArray<keyof ISanityVendorConfigs> = [
  PosVendors.CARROLLS,
  PosVendors.CARROLS_DELIVERY,
  PosVendors.NCR,
  PosVendors.NCR_DELIVERY,
  PosVendors.QDI,
  PosVendors.QDI_DELIVERY,
];

const isVendorKey = (key: string): key is keyof ISanityVendorConfigs =>
  lineItemVendors.includes(key as keyof ISanityVendorConfigs);

// Vendor configs come from two differently-shaped sources depending on which
// pipeline fetched the offer/reward: raw Sanity/GROQ documents (bookkeeping
// fields prefixed `_`, e.g. `_type`) or GraphQL query responses (`__typename`).
// Neither is part of any mutation's input schema. The legacy order-selection
// mutation tolerated them showing up in vendorConfigs, but the loyalty-service
// mutation validates input objects strictly and rejects unknown fields (e.g.
// `Field "_type" is not defined by type "LoyaltyServiceOrderSelectionVendorConfigInput"`).
// Strip them recursively so a vendor config is safe to send to either
// mutation regardless of where it was fetched from. See BKPE-27933.
const NON_INPUT_METADATA_KEYS = new Set([
  '_type',
  '_key',
  '_id',
  '_rev',
  '_createdAt',
  '_updatedAt',
  '__typename',
]);

const stripNonInputMetadata = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return (value.map(stripNonInputMetadata) as unknown) as T;
  }
  if (value && typeof value === 'object') {
    return (Object.entries(value as Record<string, unknown>).reduce((accum, [key, val]) => {
      if (!NON_INPUT_METADATA_KEYS.has(key)) {
        accum[key] = stripNonInputMetadata(val);
      }
      return accum;
    }, {} as Record<string, unknown>) as unknown) as T;
  }
  return value;
};

// filter invalid vendors
export const filterVendorConfigs = (vendorConfig: ISanityVendorConfigs | null | undefined) =>
  Object.keys(vendorConfig ?? {}).reduce<Record<string, any>>((accum, vendorKey) => {
    if (isVendorKey(vendorKey)) {
      accum[vendorKey] = stripNonInputMetadata(vendorConfig?.[vendorKey]);
    }

    return accum;
  }, {});

const getFilterBenefitsVendorConfigs = <IncentiveObject extends readonly (object | null)[]>(
  incentiveBenefits: IncentiveObject | null | undefined
) => {
  return incentiveBenefits?.reduce<Array<ILoyaltyRbiVendorConfigsInput>>((acc, incentive) => {
    if (incentive && (isItem(incentive) || isCombo(incentive)) && incentive?.vendorConfigs) {
      acc.push(filterVendorConfigs(incentive.vendorConfigs));
    } else if (isPicker(incentive)) {
      // if picker collect all options' vendor configs
      const pickerBenefitVendorConfigs = getFilterBenefitsVendorConfigs(
        incentive.options.map(({ option }) => option)
      );

      if (pickerBenefitVendorConfigs?.length) {
        acc.push(...pickerBenefitVendorConfigs);
      }
    }

    return acc;
  }, []);
};

const getIncentiveVendorConfigs = (
  cartEntry: ILegacyCartEntry
): { incentiveVendorConfigs: Array<ILoyaltyRbiVendorConfigsInput> } | null => {
  const incentives =
    cartEntry.type === ICartEntryType.OFFER
      ? cartEntry.details.offer.incentives
      : cartEntry.details.reward.incentives;

  const incentiveVendorConfigs:
    | Array<ILoyaltyRbiVendorConfigsInput>
    | undefined = getFilterBenefitsVendorConfigs(
    Array.isArray(incentives) ? incentives : undefined
  );

  return incentiveVendorConfigs?.length ? { incentiveVendorConfigs } : null;
};

const getNameFromCartEntry = (offerCartEntry: ICartEntryOffer): string => {
  const { offer } = offerCartEntry.details;
  const stringOfferName = blockContentToPlainText(offer?.name?.localeRaw || []);

  return stringOfferName;
};

const getLocalizedPropFromCartEntry = (
  cartEntry: ICartEntryOffer,
  propName: 'name' | 'description'
): Pick<ILoyaltyInRestaurantIncentiveInput, 'name' | 'description'> | null => {
  if (!propName) {
    return null;
  }
  const offer = cartEntry.details.offer;
  const stringOffer = blockContentToPlainText(offer[propName]?.localeRaw || []);

  return { [propName]: stringOffer };
};

const rewardOrderLineItem = (cartEntry: ICartEntryReward): ILoyaltyInRestaurantOrderLineItem => {
  const { quantity, referenceId, type } = cartEntry;
  const details = cartEntry.details;
  const reward = details.reward;

  return {
    incentive: {
      id: getInRestaurantRewardId(details),
      ...getIncentiveVendorConfigs(cartEntry),
      type,
    },
    productId: getInRestaurantRewardDocumentId(details),
    quantity,
    referenceId,
    vendorConfigs: filterVendorConfigs(reward.vendorConfigs as ISanityVendorConfigs),
  };
};

const offerOrderLineItem = (cartEntry: ICartEntryOffer): ILoyaltyInRestaurantOrderLineItem => {
  const { quantity, referenceId, type } = cartEntry;
  const { offer } = cartEntry.details;
  const { shortCode: offerCode } = offer;
  let id: string;

  if (cartEntry.type === ICartEntryType.OFFER) {
    id = cartEntry.details.offer.loyaltyEngineId!;
  } else {
    id = cartEntry.details.offer._id;
  }
  const name = getNameFromCartEntry(cartEntry);

  const offerDiscountIncentive =
    'incentives' in offer &&
    (offer.incentives?.find(
      incentive => incentive?.__typename === LegacyCartEntryTypeGateway.OFFERDISCOUNT
    ) as IOfferDiscount);

  return {
    incentive: {
      id,
      ...getLocalizedPropFromCartEntry(cartEntry, 'name'),
      ...getLocalizedPropFromCartEntry(cartEntry, 'description'),
      ...getIncentiveVendorConfigs(cartEntry),
      name,
      type,
      ...(offerCode && { offerCode }),
      ...(offerDiscountIncentive && {
        discount: {
          type:
            offerDiscountIncentive.discountType?.toUpperCase() ===
            LoyaltyCartDiscountType.PERCENTAGE
              ? LoyaltyCartDiscountType.PERCENTAGE
              : LoyaltyCartDiscountType.AMOUNT,
          value: offerDiscountIncentive.discountValue || 0,
        },
      }),
    },
    quantity,
    productId: offer._id,
    referenceId,
    vendorConfigs: filterVendorConfigs(offer.vendorConfigs as ISanityVendorConfigs),
  };
};

export const generateOrderLineItems = (cart: ICart): Array<ILoyaltyInRestaurantOrderLineItem> =>
  cart.map((cartEntry: ILegacyCartEntry) => {
    if (cartEntry.type === ICartEntryType.REWARD) {
      return rewardOrderLineItem(cartEntry);
    }

    return offerOrderLineItem(cartEntry);
  });

export const getAppliedOffersFromInRestaurantCart = (
  inRestaurantCart: ICart
): LoyaltyAppliedOffer[] => {
  return inRestaurantCart.reduce<LoyaltyAppliedOffer[]>((acc: LoyaltyAppliedOffer[], cartEntry) => {
    if (cartEntry.type === ICartEntryType.OFFER) {
      const { offer } = cartEntry.details;
      acc.push({
        id: offer.loyaltyEngineId,
        cartId: offer._id,
        isStackable: offer.isStackable,
        cmsId: offer._id,
      });
    }
    return acc;
  }, []);
};

// Polling interval when the user is in a different screen, in milliseconds
export const NOT_IN_MY_CODE_SCREEN_POLLING_INTERVAL_MS = 60000;
